import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
	type ConnectionItem,
	useOrganizations,
	useUpdateConnection,
} from "@/lib/api";
import { useAuthState } from "@/lib/auth-state";

/**
 * Who can use a connection. In a team workspace its creator can share it or
 * keep it private; everyone else, and every personal workspace, sees the
 * current access read-only. Sharing
 * is confirmed first because it exposes the connection's data and actions.
 */
export function ConnectionShareSwitch({
	connection,
}: {
	connection: ConnectionItem;
}) {
	const labelId = useId();
	const { session } = useAuthState();
	const { data: organizations } = useOrganizations();
	const updateConnection = useUpdateConnection();
	const [confirmingShare, setConfirmingShare] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const workspace = organizations?.find(
		(org) => org.id === connection.organization_id,
	);
	const workspaceName = workspace?.name ?? "this workspace";
	const isCreator = Boolean(
		session?.user?.id && session.user.id === connection.created_by,
	);
	const shared = connection.visibility === "org";

	// A personal workspace has nobody to share with.
	if (!isCreator || workspace?.is_personal) {
		return (
			<p className="text-sm">
				<span className="text-muted-foreground">Access: </span>
				{shared ? `Shared with ${workspaceName}` : "Private"}
			</p>
		);
	}

	const setVisibility = (visibility: ConnectionItem["visibility"]) => {
		setError(null);
		updateConnection.mutate(
			{ connection_id: connection.id, visibility },
			{
				onSuccess: () => setConfirmingShare(false),
				onError: (err) =>
					setError(err.message || "Could not change who can use this."),
			},
		);
	};

	return (
		<div className="space-y-2 text-sm">
			<div className="flex items-center gap-3">
				<Switch
					aria-labelledby={labelId}
					checked={shared || confirmingShare}
					disabled={updateConnection.isPending}
					onCheckedChange={(next) => {
						setError(null);
						if (next) setConfirmingShare(true);
						else if (confirmingShare) setConfirmingShare(false);
						else setVisibility("private");
					}}
				/>
				<span id={labelId}>Share with {workspaceName}</span>
			</div>
			{confirmingShare && !shared ? (
				<div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs">
					<span className="min-w-0 flex-1">
						Everyone in {workspaceName} will be able to see this connection's
						data and run actions through it.
					</span>
					<Button
						size="sm"
						disabled={updateConnection.isPending}
						onClick={() => setVisibility("org")}
					>
						Share
					</Button>
					<Button
						size="sm"
						variant="ghost"
						disabled={updateConnection.isPending}
						onClick={() => setConfirmingShare(false)}
					>
						Cancel
					</Button>
				</div>
			) : null}
			{error ? (
				<p role="alert" className="text-sm text-destructive">
					{error}
				</p>
			) : null}
		</div>
	);
}
